import { useFormStore } from '../../lib/store';
import ModuleCard from '../ModuleCard';
import clsx from 'clsx';
import { IconCheck, IconImage, IconRuler } from '../Icons';

interface ShirtSelectionModuleProps {
  module: {
    id: string;
    name: string;
    nameEn?: string;
    fields: any[];
    settings: {
      showTypeSelection?: boolean;
      typeOptions?: { value: string; label: string; imageUrl?: string }[];
      showSizeChart?: boolean;
      sizeChartImageUrl?: string;
      sizeChartCaption?: string;
      adultSizes?: string[];
      kidsSizes?: string[];
      kidsLabel?: string;
    };
  };
  event: any;
}

export default function ShirtSelectionModule({ module }: ShirtSelectionModuleProps) {
  const { formData, setFormValue, errors, clearError } = useFormStore();
  const settings = module.settings || {};
  const fields = module.fields || [];

  const selectedType = formData['shirt_type'];
  const selectedSize = formData['shirt_size'];
  const shirtTypeField = fields.find((f: any) => f.id === 'shirt_type');
  const shirtSizeField = fields.find((f: any) => f.id === 'shirt_size');

  const typeOptions = settings.typeOptions || (shirtTypeField?.options?.map((opt: any) => ({ value: opt.value, label: opt.label, imageUrl: opt.imageUrl })) || []);
  const showTypeSelection = settings.showTypeSelection || (typeOptions.length > 0 && shirtTypeField?.required);
  const adultSizes = settings.adultSizes || (shirtSizeField?.options?.map((opt: any) => opt.value) || ['XS', 'S', 'M', 'L', 'XL']);
  const kidsSizes = settings.kidsSizes || [];
  const sizeError = errors['shirt_size'];
  const typeError = errors['shirt_type'];

  const pick = (id: string, v: string) => { setFormValue(id, v); clearError(id); };

  const SizePill = ({ size }: { size: string }) => {
    const on = selectedSize === size;
    return (
      <label className={clsx(
        'h-11 rounded-full border-2 flex items-center justify-center text-[14px] font-bold cursor-pointer transition-colors',
        on ? 'border-primary bg-primary-soft text-primary' : 'border-transparent bg-soft text-ink',
      )}>
        <input type="radio" name="shirt_size" value={size} checked={on} onChange={() => pick('shirt_size', size)} className="sr-only" />
        {size}
      </label>
    );
  };

  return (
    <ModuleCard title={module.name}>
      {showTypeSelection && typeOptions.length > 0 && (
        <div id="field-shirt_type" className="flex flex-col gap-2">
          <span className="text-[13px] font-bold">
            {shirtTypeField?.label || '款式'}{shirtTypeField?.required && <span className="text-bad-dot ml-1" aria-hidden="true">*</span>}
          </span>
          <div role="radiogroup" aria-label={shirtTypeField?.label || '款式'} className="grid grid-cols-2 gap-3">
            {typeOptions.map((option: any) => {
              const on = selectedType === option.value;
              return (
                <label key={option.value} className={clsx(
                  'flex flex-col rounded-[14px] overflow-hidden border-2 cursor-pointer transition-colors',
                  on ? 'border-primary bg-primary-soft' : 'border-transparent bg-soft',
                )}>
                  <input type="radio" name="shirt_type" value={option.value} checked={on} onChange={() => pick('shirt_type', option.value)} className="sr-only" />
                  {option.imageUrl ? (
                    <img src={option.imageUrl} alt="" className="w-full h-28 object-cover" />
                  ) : (
                    <div className="h-28 bg-white/60 flex items-center justify-center text-muted"><IconImage size={26} /></div>
                  )}
                  <div className="flex items-center justify-between px-3 py-2.5">
                    <span className="text-[14px] font-bold">{option.label}</span>
                    {on && <IconCheck size={18} className="text-primary" />}
                  </div>
                </label>
              );
            })}
          </div>
          {typeError && <p role="alert" className="text-xs text-bad">{typeError}</p>}
        </div>
      )}

      {settings.showSizeChart && (
        <figure className="flex flex-col gap-2">
          {settings.sizeChartImageUrl ? (
            <img src={settings.sizeChartImageUrl} alt="尺寸對照表" className="w-full rounded-tile" />
          ) : (
            <div className="flex items-center gap-2 px-4 py-3 rounded-tile bg-soft text-muted text-[13px]"><IconRuler size={18} />尺寸對照表</div>
          )}
          {settings.sizeChartCaption && <figcaption className="text-xs text-muted">{settings.sizeChartCaption}</figcaption>}
        </figure>
      )}

      <div id="field-shirt_size" className="flex flex-col gap-2">
        <span className="text-[13px] font-bold">
          {shirtSizeField?.label || '尺寸'}{shirtSizeField?.required && <span className="text-bad-dot ml-1" aria-hidden="true">*</span>}
        </span>
        <div role="radiogroup" aria-label={shirtSizeField?.label || '尺寸'} className="grid grid-cols-5 gap-2">
          {adultSizes.map((size: string) => <SizePill key={size} size={size} />)}
        </div>
        {kidsSizes.length > 0 && (
          <>
            <span className="text-xs text-muted mt-2">{settings.kidsLabel || '兒童尺寸'}</span>
            <div role="radiogroup" aria-label={settings.kidsLabel || '兒童尺寸'} className="grid grid-cols-4 gap-2">
              {kidsSizes.map((size: string) => <SizePill key={size} size={size} />)}
            </div>
          </>
        )}
        {sizeError && <p role="alert" className="text-xs text-bad">{sizeError}</p>}
      </div>
    </ModuleCard>
  );
}
